import { Location } from '@angular/common';
import { Component, computed, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AppRoutes } from '@helpers/app-routes';
import { SvgComponent } from '@components/svg.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomerService } from '@services/customer.service';
import { MealPlanService } from '@services/meal-plan.service';
import { UserCompanyDataService } from '@services/user-company-data.service';
import { UserEnterpriseDataService } from '@services/user-enterprise-data.service';
import { ToastrService } from 'ngx-toastr';
import { HistoryService } from '@services/history.service';
import { FormatPricePipe } from '@pipes/format-price.pipe';

@Component({
  selector: 'app-order-header',
  templateUrl: './order-header.component.html',
  imports: [RouterOutlet, TranslateModule, SvgComponent, FormatPricePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'flex flex-col h-full',
  },
})
export class OrderHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly customerService = inject(CustomerService);
  private readonly mealPlanService = inject(MealPlanService);
  private readonly userEnterpriseDataService = inject(UserEnterpriseDataService);
  private readonly userCompanyDataService = inject(UserCompanyDataService);
  private readonly historyService = inject(HistoryService);

  protected readonly customer = computed(() => this.customerService.currentCustomer());
  protected readonly currentMealPlan = computed(() => this.mealPlanService.currentMealPlan());
  protected readonly userEnterpriseData = computed(() =>
    this.userEnterpriseDataService.currentUserEnterpriseData(),
  );
  protected readonly userCompanyData = computed(() =>
    this.userCompanyDataService.currentUserCompanyData(),
  );
  protected readonly paymentTypeKey = computed(() => {
    const type = this.userCompanyData()?.payment_type;
    return type ? `payment_type.${type.toLowerCase()}` : null;
  });

  ngOnInit(): void {
    const customer = this.customer();

    if (!customer) {
      this.#redirectWithError();
      return;
    }

    this.userEnterpriseDataService.getUserEnterpriseData(customer.id).subscribe({
      error: () => this.#redirectWithError(),
    });

    this.userCompanyDataService.getUserCompanyData(customer.id).subscribe({
      next: (data) => {
        if (!data?.payment_type) {
          this.#redirectWithError();
        }
      },
      error: () => this.#redirectWithError(),
    });
  }

  goBack(): void {
    this.historyService.back();
  }

  cancel(): void {
    void this.router.navigate([AppRoutes.scanPage]);
  }

  protected getBalanceColor(): string {
    const currency = this.customer()?.wallet?.currency;
    if (currency && currency > 0) return 'fg-vert-1';
    if (currency && currency < 0) return 'fg-rose-1';
    return 'fg-gris-1';
  }

  #redirectWithError(): void {
    this.toastr.error(
      this.translate.instant('order_header.errors.fetch_client_message'),
      this.translate.instant('order_header.errors.fetch_client_title'),
    );
    void this.router.navigate([AppRoutes.scanPage]);
  }
}
